/**
 * useTTS — Text-to-Speech accessibility hook using Web Speech API.
 *
 * Provides a toggleable read-aloud system for visually impaired players.
 * Persists preference in localStorage. Uses the browser's built-in
 * speechSynthesis (zero cost, works offline).
 *
 * Usage:
 *   const { ttsEnabled, toggleTTS, speak, stop, speaking } = useTTS();
 *   speak('narration text here');  // queues speech
 *   stop();                        // interrupts current speech
 */

import { useState, useCallback, useEffect, useRef } from 'react';

const STORAGE_KEY = 'rigel-tts-enabled';

// Preferred voices (British English first, then any English)
function pickVoice() {
  if (typeof window === 'undefined' || !window.speechSynthesis) return null;
  const voices = window.speechSynthesis.getVoices();
  // Prefer a British English male voice for Harper's world
  const british = voices.find(v => v.lang === 'en-GB' && /male|daniel|george|james/i.test(v.name));
  if (british) return british;
  const anyBritish = voices.find(v => v.lang === 'en-GB');
  if (anyBritish) return anyBritish;
  const anyEnglish = voices.find(v => v.lang.startsWith('en'));
  return anyEnglish || voices[0] || null;
}

export default function useTTS() {
  const [ttsEnabled, setTtsEnabled] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem(STORAGE_KEY) === 'true';
  });
  const [speaking, setSpeaking] = useState(false);
  const voiceRef = useRef(null);
  const utteranceRef = useRef(null);

  // Load voices (they may load async)
  useEffect(() => {
    if (typeof window === 'undefined' || !window.speechSynthesis) return;
    const loadVoices = () => { voiceRef.current = pickVoice(); };
    loadVoices();
    window.speechSynthesis.addEventListener('voiceschanged', loadVoices);
    return () => window.speechSynthesis.removeEventListener('voiceschanged', loadVoices);
  }, []);

  // Persist preference
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, ttsEnabled ? 'true' : 'false');
    }
  }, [ttsEnabled]);

  // Stop speech on unmount
  useEffect(() => {
    return () => {
      if (typeof window !== 'undefined' && window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  const toggleTTS = useCallback(() => {
    setTtsEnabled(prev => {
      const next = !prev;
      if (!next && typeof window !== 'undefined' && window.speechSynthesis) {
        window.speechSynthesis.cancel();
        setSpeaking(false);
      }
      return next;
    });
  }, []);

  const stop = useCallback(() => {
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      window.speechSynthesis.cancel();
      setSpeaking(false);
    }
  }, []);

  const speak = useCallback((text, { rate = 0.95, pitch = 1.0, priority = false } = {}) => {
    if (!ttsEnabled) return;
    if (typeof window === 'undefined' || !window.speechSynthesis) return;
    if (!text || !text.trim()) return;

    // If priority, cancel current speech; otherwise queue
    if (priority) {
      window.speechSynthesis.cancel();
    }

    // Clean text for speech — strip markdown-like formatting, multiple spaces
    const cleanText = text
      .replace(/[─━═▌│┤├┐┘└┌]/g, '')  // box-drawing chars
      .replace(/[*_~`#>]/g, '')         // markdown
      .replace(/\s+/g, ' ')            // collapse whitespace
      .trim();

    if (!cleanText) return;

    const utterance = new SpeechSynthesisUtterance(cleanText);
    utterance.rate = rate;
    utterance.pitch = pitch;
    utterance.volume = 1.0;
    if (voiceRef.current) utterance.voice = voiceRef.current;

    utterance.onstart = () => setSpeaking(true);
    utterance.onend = () => setSpeaking(false);
    utterance.onerror = () => setSpeaking(false);

    utteranceRef.current = utterance;
    window.speechSynthesis.speak(utterance);
  }, [ttsEnabled]);

  // Convenience: speak a list of choices
  const speakChoices = useCallback((choices) => {
    if (!ttsEnabled || !choices?.length) return;
    const choiceText = choices.map((c, i) =>
      `Option ${i + 1}: ${c.command}. ${c.audio_cue || ''}`
    ).join('. ');
    speak(`What do you do? ${choiceText}`, { rate: 0.9 });
  }, [ttsEnabled, speak]);

  const supported = typeof window !== 'undefined' && !!window.speechSynthesis;

  return { ttsEnabled, toggleTTS, speak, speakChoices, stop, speaking, supported };
}
